'use client';

import { useEffect, useRef, useState } from 'react';
import { animate, motion, useInView, useReducedMotion } from 'framer-motion';

/**
 * The Features headline as a chart: jobs per day for the same team, a week
 * before and a week after. The two averages differ by exactly 35%, so the
 * picture says what the headline says and nothing more. Figures are an index
 * (before = 100), not a customer's data.
 */
const BEFORE = [97, 103, 94, 100, 106, 98, 102]; // mean 100
const AFTER = [129, 139, 132, 142, 135, 131, 137]; // mean 135
const TOP = 150;
const EASE = [0.22, 1, 0.36, 1];
const mean = (a) => a.reduce((s, v) => s + v, 0) / a.length;

function Count({ to, start }) {
  const reduce = useReducedMotion();
  const [n, setN] = useState(reduce ? to : 0);
  useEffect(() => {
    if (!start || reduce) return;
    const controls = animate(0, to, {
      duration: 1.6,
      ease: EASE,
      delay: 0.5,
      onUpdate: (v) => setN(Math.round(v)),
    });
    return () => controls.stop();
  }, [start, to, reduce]);
  return <>{n}</>;
}

function Week({ values, tone, label, avgTone, delay, start }) {
  const reduce = useReducedMotion();
  const avg = mean(values);
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="relative flex h-[200px] items-end gap-1.5 sm:h-[240px] sm:gap-2">
        {values.map((v, i) => (
          <motion.div
            key={i}
            className={`flex-1 rounded-t-[4px] ${tone}`}
            initial={reduce ? false : { height: 0 }}
            animate={start ? { height: `${(v / TOP) * 100}%` } : undefined}
            transition={{ duration: 0.8, ease: EASE, delay: delay + i * 0.05 }}
          />
        ))}
        {/* the week's average */}
        <motion.div
          className={`absolute inset-x-0 origin-left border-t border-dashed ${avgTone}`}
          style={{ bottom: `${(avg / TOP) * 100}%` }}
          initial={reduce ? false : { scaleX: 0 }}
          animate={start ? { scaleX: 1 } : undefined}
          transition={{ duration: 0.7, ease: EASE, delay: delay + 0.5 }}
        />
      </div>
      <div className="mt-3 flex items-baseline justify-between border-t border-secondary-dark/10 pt-2.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-secondary-dark/50">
          {label}
        </span>
        <span className="font-mono text-xs text-secondary-dark">avg {Math.round(avg)}</span>
      </div>
    </div>
  );
}

export default function ThroughputChart() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <div
      ref={ref}
      className="flex h-full min-h-[380px] flex-col rounded-[1.75rem] border border-secondary-dark/[0.07] bg-white p-6 shadow-[0_40px_100px_-40px_rgba(2,32,71,0.35)] sm:p-8"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label text-[10px] text-secondary-dark/45">Jobs per day · same team</div>
          <div className="mt-1.5 font-mono text-[10px] text-secondary-dark/35">
            Index, before = 100
          </div>
        </div>
        <div className="display text-[clamp(44px,4.6vw,72px)] text-primary-blue">
          +<Count to={35} start={inView} />%
        </div>
      </div>

      <div className="mt-auto flex gap-5 pt-8 sm:gap-8">
        <Week
          values={BEFORE}
          tone="bg-secondary-dark/15"
          avgTone="border-secondary-dark/40"
          label="Before"
          delay={0}
          start={inView}
        />
        <Week
          values={AFTER}
          tone="bg-primary-blue"
          avgTone="border-ink"
          label="With LenSync"
          delay={0.35}
          start={inView}
        />
      </div>
    </div>
  );
}
